import {Anchor, Heading, Icon, SplitCard} from '@albert-io/atomic';
import cx from 'classnames';
import {kebabCase} from 'lodash';
import React, {useRef, useState} from 'react';

import {PracticeSettings} from 'client/AdaptiveSubjects/AdaptiveSubjectsHeader/PracticeSettings/PracticeSettings';
import SubjectSplitCard from 'client/components/SubjectSplitCard/SubjectSplitCard.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';

import './adaptive-subject-course-card.scss';

interface AdaptiveSubjectCourseCardProps {
  className?: string;
  isLoggedOutOrStudent?: boolean;
  subjectId: string;
  subjectName: string;
  subjectUrlSlug: string;
  subjectDescription: string;
  curriculumAreaName: string;
  'data-testid'?: string;
}

export const AdaptiveSubjectCourseCard = ({
  className,
  isLoggedOutOrStudent,
  subjectId,
  subjectName,
  subjectUrlSlug,
  subjectDescription,
  curriculumAreaName,
  'data-testid': dataTestId
}: AdaptiveSubjectCourseCardProps) => {
  const [showDescription, setShowDescription] = useState(false);

  const linkRef = useRef(null);
  const descriptionRef = useRef(null);

  const curriculumAreaSlug = kebabCase(curriculumAreaName);
  const showActionBar = !isLoggedOutOrStudent || masqueradeStore.isMasquerading();

  const subjectHref = `/adaptive-subjects/${subjectUrlSlug}`;

  // Generate dynamic test IDs based on exact subject name
  const getTestId = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : `${subjectName}-${suffix}`;

  const actionBar = (
    <Anchor
      role='switch'
      as='button'
      className='u-text-align_right u-font-weight_bold'
      inheritColor
    >
      Practice Settings
      <Icon icon='caret-down' className='u-mar-l_1' />
    </Anchor>
  );

  return (
    <SubjectSplitCard
      className={cx(className, {
        'subject-course-card--description-in-view': showDescription,
        'subject-course-card--action-bar-in-view': showActionBar
      })}
      key={subjectId}
    >
      <SubjectSplitCard.Top
        className={cx(
          `subject-course-card__details u-padding-inline_space-x3 u-padding-block_space-x3 u-color_white u-overflow_hidden flex items-end background-domain-${curriculumAreaSlug}`
        )}
        onClick={(e) => {
          const link = linkRef.current;
          const description = descriptionRef.current;
          if (link && link !== e.target && description !== e.target) {
            (link as any).click();
          }
        }}
        data-testid={getTestId('card')}
      >
        <SubjectSplitCard.Title className='flex flex-col'>
          <Heading className={cx({'subject-course-card__title': !!subjectDescription})}>
            {/* @ts-ignore innerRef exists */}
            <Anchor innerRef={linkRef} to={subjectHref} inheritColor>
              {subjectName}
            </Anchor>
          </Heading>
          <Anchor
            as='button'
            onClick={() => setShowDescription((prevShowDescription) => !prevShowDescription)}
            inheritColor
            className='subject-course-card__description-link'
            role='switch'
            aria-checked={showDescription}
            ref={descriptionRef}
            data-testid={getTestId('show-description-btn')}
          >
            {showDescription ? 'Hide description' : 'Show description'}
          </Anchor>
        </SubjectSplitCard.Title>
      </SubjectSplitCard.Top>
      <SplitCard.Section
        aria-hidden={!showDescription}
        className='subject-course-card__description u-padding-inline_space-x3 u-overflow_hidden grow'
      >
        <MarkdownRendererV2 text={subjectDescription} />
      </SplitCard.Section>
      {showActionBar && (
        <SubjectSplitCard.Bottom
          paddingLevel={0}
          border='none'
          className='subject-course-card__action-bar u-padding-inline_space-x3 u-padding-block_space-x1 flex justify-end content-secondary'
        >
          <PracticeSettings
            subjectId={subjectId}
            trigger={actionBar}
            className='adaptive-subject-course-card__practice-settings u-font-weight_normal u-text-align_center'
            data-testid={getTestId('practice-settings-btn')}
          />
        </SubjectSplitCard.Bottom>
      )}
    </SubjectSplitCard>
  );
};
